import { handleWidgetRenderRequest } from '@/components/dashboard/widgetRenderProxy';
import { api } from '@/lib/api';
import {
  type ArtifactDashboardSpecRequest,
  type ArtifactRpcMethod,
  ArtifactRpcMethodSchema,
  type ArtifactRpcResponse,
} from '@innermate/shared';
import { type RefObject, useEffect } from 'react';

/** iframe RPC 를 서버로 라우팅하는 함수. 기본은 사용자 경로(`api.artifactRpc`), 관리자 검토 패널은
 *  세션 소유자 스코프로 실행하는 `api.adminArtifactRpc` 바인딩을 주입한다. */
export type ArtifactRpcFn = (
  artifactId: string,
  method: ArtifactRpcMethod,
  params: unknown,
) => Promise<ArtifactRpcResponse>;

interface BridgeOptions {
  /**
   * 서버가 runSavedQuery RPC 에 `code:'query_expired'` 로 응답한 경우 호출. 정규식 추출로
   * 잡히지 않은 동적 queryId (변수 경유) 호출에서도 대시보드 만료를 감지하기 위한 2차 채널.
   * iframe 에는 기존 에러 메시지가 그대로 전달되고, 이 콜백은 부모 UI (패널) 에서 오버레이를
   * 활성화하는 용도.
   */
  onQueryExpired?: (queryId: string | null) => void;
  /** RPC 라우팅 함수 오버라이드. 미지정 시 `api.artifactRpc` (사용자 경로). */
  rpc?: ArtifactRpcFn;
  /**
   * 채팅 대시보드 수동 편집 영속화용 — 현재 표시 중인 버전 번호. iframe 이
   * `dashboard.artifact.spec.changed` 를 보내면 이 버전의 content + spec 을 in-place 갱신한다.
   * rpc 오버라이드(관리자 검토) 경로에서는 영속화하지 않는다(소유자 아님).
   */
  dashboardVersion?: number;
}

/**
 * 부모측 훅. iframe 이 `window.parent.postMessage({ reqId, method, params })` 를 보내면
 * 서버 `/api/artifacts/:id/rpc` 로 프록시하고 `{ reqId, ok, data | error }` 응답을 돌려준다.
 *
 * - `event.source` 를 iframe.contentWindow 와 엄격 비교 (다른 iframe 메시지 무시)
 * - method 는 서버에서도 zod enum 으로 재검증되지만, 네트워크 왕복 전에 여기서 한 번 필터해 무효 호출을 빠르게 차단
 * - reqId 로 다중 동시 RPC 지원 (iframe 쪽이 Map 으로 추적)
 */
export function useArtifactBridge(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  artifactId: string,
  options: BridgeOptions = {},
): void {
  const { onQueryExpired, rpc, dashboardVersion } = options;
  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      const iframe = iframeRef.current;
      if (!iframe || ev.source !== iframe.contentWindow) return;
      const d = ev.data;
      if (!d || typeof d !== 'object') return;

      // dashboard 의 client-side widget mutation 매개 (chat artifact 의 dashboard 도 동일 endpoint 사용).
      const kind = (d as { kind?: unknown }).kind;
      if (kind === 'dashboard.widget-render-request') {
        handleWidgetRenderRequest(iframe, {
          reqId: (d as { reqId?: unknown }).reqId,
          payload: (d as { payload?: unknown }).payload,
        });
        return;
      }

      // 채팅 대시보드 수동 편집 영속화 (fire-and-forget). 사용자 경로(rpc 미지정)에서만,
      // 현재 버전이 있을 때. 모델 dashboard.edit 인덱스가 수동 변경을 반영하도록.
      if (kind === 'dashboard.artifact.spec.changed') {
        if (!rpc && typeof dashboardVersion === 'number') {
          const spec = (d as { spec?: unknown }).spec;
          void api
            .artifactSetDashboardSpec(artifactId, {
              version: dashboardVersion,
              spec: spec as ArtifactDashboardSpecRequest['spec'],
            })
            .catch(() => {
              /* 영속화 실패는 무시 — UI 는 이미 반영됨, 새로고침 시 복원 */
            });
        }
        return;
      }

      const reqId = (d as { reqId?: unknown }).reqId;
      const methodRaw = (d as { method?: unknown }).method;
      const params = (d as { params?: unknown }).params;
      if (typeof reqId !== 'number') return;

      const replyOk = (data: unknown) => {
        iframe.contentWindow?.postMessage({ reqId, ok: true, data }, '*');
      };
      const replyErr = (error: string) => {
        iframe.contentWindow?.postMessage({ reqId, ok: false, error }, '*');
      };

      const parsedMethod = ArtifactRpcMethodSchema.safeParse(methodRaw);
      if (!parsedMethod.success) {
        replyErr(`알 수 없는 method: ${String(methodRaw)}`);
        return;
      }

      void (async () => {
        try {
          const res = await (rpc ?? api.artifactRpc)(artifactId, parsedMethod.data, params);
          if (res.ok) {
            replyOk(res.data);
          } else {
            if (res.code === 'query_expired' && onQueryExpired) {
              const qid =
                params && typeof params === 'object' && params !== null
                  ? (params as { queryId?: unknown }).queryId
                  : undefined;
              onQueryExpired(typeof qid === 'string' ? qid : null);
            }
            replyErr(res.error);
          }
        } catch (err) {
          replyErr(err instanceof Error ? err.message : 'RPC 호출 실패');
        }
      })();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [iframeRef, artifactId, onQueryExpired, rpc, dashboardVersion]);
}
